/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
  
  import {$applyNodeReplacement, DecoratorNode} from 'lexical';
  import * as React from 'react';
  import {Suspense} from 'react';
  
  const ImageComponent = React.lazy(() => import('./ImageComponent'));
  
  function isGoogleDocCheckboxImg(img) {
    return (
      img.parentElement != null &&
      img.parentElement.tagName === 'LI' &&
      img.previousSibling === null &&
      img.getAttribute('aria-roledescription') === 'checkbox'
    );
  }
  
  function $convertImageElement(domNode) {
    const img = domNode;
    if (img.src.startsWith('file:///') || isGoogleDocCheckboxImg(img)) {
      return null;
    }
    const {alt: altText, src, width, height} = img;
    const node = $createImageNode({altText, height, src, width});
    return {node};
  }
  
  export class ImageNode extends DecoratorNode {
    __src;
    __altText;
    __width;
    __height;
    __maxWidth;
  
    static getType() {
      return 'image';
    }
  
    static clone(node) {
      return new ImageNode(
        node.__src,
        node.__altText,
        node.__maxWidth,
        node.__width,
        node.__height,
        node.__key,
      );
    }
  
    static importJSON(serializedNode) {
      const {altText, height, width, maxWidth, src} =
        serializedNode;
      const node = $createImageNode({
        altText,
        height,
        maxWidth,
        src,
        width,
      });
      return node;
    }
  
    exportDOM() {
      const element = document.createElement('img');
      element.setAttribute('src', this.__src);
      element.setAttribute('alt', this.__altText);
      element.setAttribute('width', this.__width.toString());
      element.setAttribute('height', this.__height.toString());
      return {element};
    }
  
    static importDOM(){
      return {
        img: (node) => ({
          conversion: $convertImageElement,
          priority: 0,
        }),
      };
    }
  
    constructor(
      src,
      altText,
      maxWidth,
      width,
      height,
      key,
    ) {
      super(key);
      this.__src = src;
      this.__altText = altText;
      this.__maxWidth = maxWidth;
      this.__width = width || 'inherit';
      this.__height = height || 'inherit';
    }
  
    exportJSON() {
      return {
        altText: this.getAltText(),
        height: this.__height === 'inherit' ? 0 : this.__height,
        maxWidth: this.__maxWidth,
        src: this.getSrc(),
        type: 'image',
        version: 1,
        width: this.__width === 'inherit' ? 0 : this.__width,
      };
    }
  
    setWidthAndHeight(
      width,
      height,
    ) {
      const writable = this.getWritable();
      writable.__width = width;
      writable.__height = height;
    }
  
    // View
  
    createDOM(config) {
      const span = document.createElement('span');
      const theme = config.theme;
      const className = theme.image;
      if (className !== undefined) {
        span.className = className;
      }
      return span;
    }
  
    updateDOM() {
      return false;
    }
  
    getSrc() {
      return this.__src;
    }
  
    getAltText() {
      return this.__altText;
    }
  
    decorate() {
      return (
        <Suspense fallback={null}>
          <ImageComponent
            src={this.__src}
            altText={this.__altText}
            width={this.__width}
            height={this.__height}
            maxWidth={this.__maxWidth}
            nodeKey={this.getKey()}
            resizable={true}
          />
        </Suspense>
      );
    }
  }
  
  export function $createImageNode({
    altText,
    height,
    maxWidth = 500,
    src,
    width,
    key,
  }) {
    return $applyNodeReplacement(
      new ImageNode(
        src,
        altText,
        maxWidth,
        width,
        height,
        key,
      ),
    );
  }
  
  export function $isImageNode(node) {
    return node instanceof ImageNode;
  }
  